import { Route } from '@unistocks/features/navigation/route';
import { isDefined, isString } from '@unistocks/shared/utils/util';
import { usePathname } from 'expo-router';

/**
 * Resolves a route's href to a concrete pathname by stripping route groups
 * (e.g. `(tabs)`) and substituting dynamic segments (e.g. `[id]`) with
 * their actual param values.
 *
 * This lets us compare against `usePathname()` which already returns the
 * fully-resolved current path (e.g. `/portfolios/abc123`).
 */
const resolveHrefPath = (href: Route['href']): string => {
  const pathname = isString(href) ? href : href.pathname;
  const params = isString(href) ? {} : ((href.params ?? {}) as Record<string, string>);

  const segments = pathname
    .split('/')
    .filter(isDefined)
    .filter(s => !(s.startsWith('(') && s.endsWith(')'))) // strip route groups like (tabs)
    .map(segment => {
      if (segment.startsWith('[') && segment.endsWith(']')) {
        const paramName = segment.slice(1, -1);
        return paramName in params ? String(params[paramName]) : segment;
      }
      return segment;
    });

  return '/' + segments.join('/');
};

const matchesPathname = (route: Route, pathname: string): boolean => {
  const resolved = resolveHrefPath(route.href);
  if (pathname === resolved || pathname.startsWith(resolved + '/')) return true;
  return route.children?.some(child => matchesPathname(child, pathname)) ?? false;
};

export const useRouteActive = (route: Route) => matchesPathname(route, usePathname());
